import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Button,
  Container,
  Box,
  TextField,
  Typography,
  Paper,
  useTheme,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const EditOrganization = () => {
  const theme = useTheme();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const { OrganizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [originalData, setOriginalData] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobileNo: '',
    landlineNo: '',
    address: '',
    zipcode: '',
    website: '',
    fax: '',
    country: '',
    state: '',
    city: '',
  });

  // Common style for all form fields
  const textFieldSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.primary.main,
      },
    },
  };

  const selectSx = {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.primary,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.primary.main,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
    },
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const response = await axios.get(`${API_BASE_URL}/api/master/countries/`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        setCountries(response.data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    const fetchStates = async () => {
      if (formData.country) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/states/?country=${formData.country}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredStates = response.data.filter(
            (state) => state.country === parseInt(formData.country)
          );
          setStates(filteredStates);
          
          // Only reset state and city if NOT initial load
          if (!isInitialLoad) {
            setFormData((prevData) => ({ ...prevData, state: '', city: '' }));
          }
        } catch (err) {
          console.error("Failed to fetch states:", err);
        }
      }
    };
    fetchStates();
  }, [formData.country]);

  // Fetch cities when state changes
  useEffect(() => {
    const fetchCities = async () => {
      if (formData.state) {
        try {
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/cities/?state=${formData.state}`,
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          const filteredCities = response.data.filter(
            (city) => city.state === parseInt(formData.state)
          );
          setCities(filteredCities);
          
          // Only reset city if NOT initial load
          if (!isInitialLoad) {
            setFormData((prevData) => ({ ...prevData, city: '' }));
          }
        } catch (err) {
          console.error("Failed to fetch cities:", err);
        }
      }
    };
    fetchCities();
  }, [formData.state]);

  // Fetch organization data
  useEffect(() => {
    const fetchOrganization = async () => {
      if (OrganizationId) {
        try {
          setLoading(true);
          const accessToken = localStorage.getItem('accessToken');
          const response = await axios.get(
            `${API_BASE_URL}/api/master/organizations/${OrganizationId}/`, 
            { headers: { 'Authorization': `Bearer ${accessToken}` } }
          );
          
          const orgData = response.data;
          
          const formattedData = {
            name: orgData.name,
            email: orgData.email,
            mobileNo: orgData.mobile,
            landlineNo: orgData.landline || '',
            address: orgData.address || '',
            zipcode: orgData.zipcode || '',
            website: orgData.website || '',
            fax: orgData.fax || '',
            country: orgData.country || '',
            state: orgData.state || '',
            city: orgData.city || '',
          };
          
          setFormData(formattedData);
          setOriginalData(formattedData);
          
          // Delay to allow cascading dropdowns to populate
          setTimeout(() => setIsInitialLoad(false), 500);
          
        } catch (err) {
          setSnackbarMessage('Failed to fetch organization details');
          setSnackbarSeverity('error');
          setOpenSnackbar(true);
          if (err.response?.status === 401) {
            localStorage.removeItem('accessToken');
            navigate('/login');
          }
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchOrganization();
  }, [OrganizationId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.mobileNo ||
        !formData.address || !formData.zipcode || !formData.website ||
        !formData.country || !formData.state || !formData.city) {
      setSnackbarMessage('Please fill all required fields');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      return;
    }

    try {
      setLoading(true);
      const accessToken = localStorage.getItem('accessToken');
      const payload = {
        name: formData.name,
        email: formData.email,
        mobile: formData.mobileNo,
        landline: formData.landlineNo,
        address: formData.address,
        zipcode: formData.zipcode,
        website: formData.website,
        fax: formData.fax,
        country: formData.country,
        state: formData.state,
        city: formData.city
      };

      await axios.put(
        `${API_BASE_URL}/api/master/organizations/${OrganizationId}/`,
        payload,
        { headers: { 'Authorization': `Bearer ${accessToken}` } }
      );

      setSnackbarMessage('Organization updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);

      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.message || 'Failed to update organization');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
      if (err.response?.status === 401) {
        localStorage.removeItem('accessToken');
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);

  const isFormValid = () => {
    return formData.name && formData.email && formData.mobileNo &&
           formData.address && formData.zipcode && formData.website &&
           formData.country && formData.state && formData.city;
  };

  return (
    <Container maxWidth="lg" sx={{ my: 0 }}>
      <Box
        component={Paper}
        elevation={3}
        sx={{ p: 4, borderRadius: 2, backgroundColor: theme.palette.background.paper }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
            Edit Organization Details
          </Typography>
        </Box>

        {loading ? (
          <Typography>Loading organization details...</Typography>
        ) : (
          <form onSubmit={handleSave}>
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Organization Name *"
                variant="outlined"
                fullWidth
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                sx={textFieldSx}
              />
              <TextField
                label="Email *"
                variant="outlined"
                fullWidth
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Mobile No. *"
                variant="outlined"
                fullWidth
                name="mobileNo"
                value={formData.mobileNo}
                onChange={handleChange}
                inputProps={{ maxLength: 10 }}
                required
                sx={textFieldSx}
              />
              <TextField
                label="Landline No."
                variant="outlined"
                fullWidth
                name="landlineNo"
                value={formData.landlineNo}
                onChange={handleChange}
                inputProps={{ maxLength: 15 }}
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Address *"
                variant="outlined"
                fullWidth
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
                sx={textFieldSx}
              />
              <TextField
                label="Pin Code *"
                variant="outlined"
                fullWidth
                name="zipcode"
                value={formData.zipcode}
                onChange={handleChange}
                required
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Website *"
                variant="outlined"
                fullWidth
                name="website"
                value={formData.website}
                onChange={handleChange}
                required
                sx={textFieldSx}
              />
              <TextField
                label="Fax"
                variant="outlined"
                fullWidth
                name="fax"
                value={formData.fax}
                onChange={handleChange}
                sx={textFieldSx}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl fullWidth required>
                <InputLabel sx={{ color: theme.palette.text.primary }}>Country *</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label="Country *"
                  sx={selectSx}
                >
                  {countries.map((country) => (
                    <MenuItem key={country.id} value={country.id}>{country.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel sx={{ color: theme.palette.text.primary }}>State *</InputLabel>
                <Select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  label="State *"
                  disabled={!formData.country || states.length === 0}
                  sx={selectSx}
                >
                  {states.map((state) => (
                    <MenuItem key={state.id} value={state.id}>{state.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <FormControl fullWidth required>
                <InputLabel sx={{ color: theme.palette.text.primary }}>City *</InputLabel>
                <Select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  label="City *"
                  disabled={!formData.state || cities.length === 0}
                  sx={selectSx}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="contained"
                type="submit"
                disabled={!isFormValid() || loading}
                sx={{ backgroundColor: theme.palette.primary.main }}
              >
                {loading ? 'UPDATING...' : 'UPDATE'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleCancel}
                disabled={loading}
              >
                CANCEL
              </Button>
            </Box>
          </form>
        )}
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default EditOrganization;
